import React, {useCallback, useEffect, useState} from "react";
import {Card, Modal} from "reactstrap";

import AppointmentsService from "services/appointments";
import StorageService from "services/StorageService";
import {USER} from "constants/storageConstants";
import {SUPER_ADMIN} from "constants/roles";
import {
    DAYS_IN_WEEK,
    FOLLOW_UP_DURATIONS,
    MENTAL_HEALTH_PLAN,
    MIN_FOLLOW_UP_LEAD_DAYS,
    OTHER_DURATION,
} from "constants/callConstants";
import {addUkDays, displayUkDate, formatUkDateTime, toUkDateString, toUkDisplayDate, ukDateToInstant} from "helpers/ukDate";
import {describeChange, describeHistoryState} from "./mentalHealthChange";

const NOT_FOUND = 404;
const NO_ACCESS = 400;

/**
 * What has happened to this consultation's reminder, as one of four states.
 *
 * `endedAt` alone cannot tell "sent" from "cancelled" - both close the row. The difference is that a
 * send leaves lastRemindedAt and no endedBy, and a cancellation leaves endedBy and no lastRemindedAt.
 * The order of these branches is the whole logic, so it lives out here where it can be tested rather
 * than inside the component.
 *
 * @param {object|null} review the mental-health review as the API returns it
 * @returns {{kind: string, label: string}|null} null when there is no reminder to describe
 */
export function describeReminder(review) {
    if (!review || !review.isMentalHealth || !review.remindAt) {
        return null;
    }

    if (review.lastRemindedAt) {
        return { kind: "sent", label: `Reminder sent on ${toUkDisplayDate(review.lastRemindedAt)}` };
    }

    if (review.endedBy) {
        const who = `${review.endedBy.firstName || ""} ${review.endedBy.lastName || ""}`.trim() || "a clinician";

        return {
            kind: "cancelled",
            label: `Reminder cancelled by ${who}${review.endedAt ? ` on ${toUkDisplayDate(review.endedAt)}` : ""}`,
        };
    }

    if (review.remindersActive) {
        return { kind: "pending", label: `Reminder scheduled for ${toUkDisplayDate(review.remindAt)}` };
    }

    // Closed, never sent, nobody named - the row was stood down by an edit rather than by a person.
    return { kind: "stopped", label: "Reminder no longer scheduled" };
}

const PLAN_LABEL = {
    [MENTAL_HEALTH_PLAN.followUp]: "Follow-up needed",
    [MENTAL_HEALTH_PLAN.repeatPlan]: "Join repeat prescription plan",
};

/**
 * The mental-health answer for one consultation, and the means to correct it.
 *
 * Deliberately NOT gated by the 24-hour window that governs the consultation notes beside it. That
 * limit exists to stop clinical narrative being rewritten after the fact; this is a single
 * categorical answer that drives a future email, and a wrong one needs correcting whenever it is
 * noticed - otherwise the only remedy is direct database access.
 *
 * Four states are shown distinctly, because they are four different facts:
 *   - never asked (404): the question predates this consultation. NOT the same as an answer of "no",
 *     and the backend stores them differently.
 *   - answered no.
 *   - answered yes, with the plan and, for a follow-up, the date.
 *   - not ours to show (400): the API only lets the treating clinician read a paid consultation, so
 *     for anyone else there is nothing to display and no point offering an Edit button that cannot
 *     work. Say so plainly rather than showing a retryable error.
 */
export const MentalHealthReviewSection = ({ appointment, patientName }) => {
    const [review, setReview] = useState(null);
    const [neverAsked, setNeverAsked] = useState(false);
    const [noAccess, setNoAccess] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [error, setError] = useState("");
    const [isMentalHealth, setIsMentalHealth] = useState(null);
    const [plan, setPlan] = useState(null);
    // Same duration presets as the call screen, so a clinician meets one idea in both places.
    const [followUpWeeks, setFollowUpWeeks] = useState(null);
    const [customFollowUpDate, setCustomFollowUpDate] = useState("");
    // Super admins only: the change waiting on the confirm step, and the answer's history.
    const [pendingChange, setPendingChange] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [changes, setChanges] = useState(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [historyError, setHistoryError] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await AppointmentsService.getMentalHealthReview(appointment.id);
            setReview(data);
            setNeverAsked(false);
            setNoAccess(false);
            setError("");
            setIsMentalHealth(data.isMentalHealth);
            setPlan(data.plan || null);

            // An existing answer already has a concrete date, and it will rarely land exactly on 1,
            // 2 or 4 weeks from today. Load it as "Other" with the real date rather than snapping to
            // the nearest preset - silently moving a follow-up the clinician already chose would be
            // worse than showing them a date picker.
            if (data.followUpDueAt) {
                setFollowUpWeeks(OTHER_DURATION);
                setCustomFollowUpDate(toUkDateString(data.followUpDueAt));
            } else {
                setFollowUpWeeks(null);
                setCustomFollowUpDate("");
            }
        } catch (e) {
            // The shared axios interceptor rejects with the parsed response BODY, not the axios
            // error - so there is no `e.response` to read a status from. The body carries the HTTP
            // status on `code`, which is what the interceptor itself keys on.
            const status = e?.code || e?.statusCode;

            setReview(null);
            setNeverAsked(status === NOT_FOUND);
            setNoAccess(status === NO_ACCESS);

            setError(status === NOT_FOUND || status === NO_ACCESS
                ? ""
                : "Could not load the mental health answer.");
        }
    }, [appointment.id]);

    useEffect(() => { load(); }, [load]);

    /**
     * Turn the chosen duration into the date the backend wants.
     *
     * @returns {string} an ISO date-time, or '' when nothing is chosen yet
     */
    function resolveFollowUpDate() {
        if (followUpWeeks === OTHER_DURATION) {
            return customFollowUpDate;
        }

        return followUpWeeks ? addUkDays(followUpWeeks * DAYS_IN_WEEK) : "";
    }

    function resolveFollowUpDueAt() {
        return ukDateToInstant(resolveFollowUpDate());
    }

    // The API accepts a cancel from the rota doctor or a super admin (any consultation), so the button
    // is offered to exactly those. appointment.userIdOfDoctor is the session doctor's users.id - the
    // exact value the backend gate compares against. The server re-checks the super admin's CURRENT
    // role, so a stale stored role can show the button but not get a cancel through.
    const currentUser = new StorageService().getValueByKey(USER);
    const currentUserId = currentUser?.id;
    const isAssignedDoctor = currentUserId === appointment.userIdOfDoctor;
    const isSuperAdmin = currentUser?.role === SUPER_ADMIN;
    const canCancelReminder = isAssignedDoctor || isSuperAdmin;

    async function cancelReminder() {
        setIsCancelling(true);

        try {
            setError("");
            await AppointmentsService.stopMentalHealthReminders(appointment.id);
            await load();
        } catch (e) {
            setError("Could not cancel the reminder. Please reload and check whether it has already been sent.");
        } finally {
            setIsCancelling(false);
        }
    }

    const reminder = describeReminder(review);
    const earliestFollowUpDate = addUkDays(MIN_FOLLOW_UP_LEAD_DAYS);

    async function save() {
        if (isMentalHealth === null) {
            setError("Choose Yes or No.");
            return;
        }
        if (isMentalHealth && !plan) {
            setError("Choose a follow-up plan.");
            return;
        }
        if (isMentalHealth && plan === MENTAL_HEALTH_PLAN.followUp) {
            const followUpDate = resolveFollowUpDate();

            if (!followUpDate) {
                setError(followUpWeeks === OTHER_DURATION
                    ? "Choose a follow-up date."
                    : "Choose when the follow-up is due.");
                return;
            }

            // An answer loaded from an old consultation arrives with its original date, which is
            // usually in the past. Re-saving it verbatim would hand the backend a due date whose
            // reminder is already overdue, and the scheduler would email the patient on its next
            // run - about a follow-up that happened months ago. Make the clinician choose again.
            if (followUpDate < earliestFollowUpDate) {
                setError(`Choose a follow-up date from ${displayUkDate(earliestFollowUpDate)} onwards. `
                    + "The patient is emailed two days beforehand, so an earlier date would send "
                    + "that email immediately.");
                return;
            }
        }

        const payload = { isMentalHealth };

        if (isMentalHealth) {
            payload.plan = plan;

            if (plan === MENTAL_HEALTH_PLAN.followUp) {
                payload.followUpDueAt = resolveFollowUpDueAt();
            }
        }

        // The SuperAdmin "Set or correct a mental-health follow-up" action: a super admin may change
        // the answer on any consultation, so the change is shown on a confirm step first - the
        // patient, the follow-up date and the email date. Clinicians save as before.
        if (isSuperAdmin) {
            setError("");
            setPendingChange({
                payload,
                summary: describeChange(review, payload, { consultationDate: appointment.date }),
            });
            return;
        }

        await submit(payload);
    }

    async function submit(payload) {
        setIsSaving(true);

        try {
            setError("");
            await AppointmentsService.setMentalHealthReview(appointment.id, payload);
            setPendingChange(null);
            setIsEditMode(false);
            await load();
            if (isHistoryOpen) {
                await loadHistory();
            }
        } catch (e) {
            setPendingChange(null);
            // The server says why when it refuses (the 3-day minimum, or not being allowed to change
            // this answer); say that rather than "try again", which would not help.
            setError(e?.message || "Could not save the mental health answer. Please try again.");
        } finally {
            setIsSaving(false);
        }
    }

    async function loadHistory() {
        try {
            setHistoryError("");
            const { data } = await AppointmentsService.getMentalHealthReviewChanges(appointment.id);
            setChanges(Array.isArray(data) ? data : []);
        } catch (e) {
            setChanges(null);
            setHistoryError("Could not load the history.");
        }
    }

    async function toggleHistory() {
        const opening = !isHistoryOpen;
        setIsHistoryOpen(opening);
        if (opening) {
            await loadHistory();
        }
    }

    return (
        <Card className="patient-management__mental-health">
            <label className="consultation-label">Mental health</label>

            {!isEditMode && (
                <div>
                    {noAccess && (
                        <p className="text-muted">
                            Only the clinician who took this consultation can view or change its
                            mental health answer.
                        </p>
                    )}
                    {neverAsked && <p className="text-muted">Not asked for this consultation.</p>}
                    {review && !review.isMentalHealth && <p>Not a mental health consultation.</p>}
                    {review && review.isMentalHealth && (
                        <>
                            <p className="mh-status__answer">
                                Mental health consultation
                                {review.plan ? ` — ${PLAN_LABEL[review.plan] || "plan set"}` : ""}
                                {review.followUpDueAt ? ` — follow-up due ${toUkDisplayDate(review.followUpDueAt)}` : ""}
                            </p>

                            {reminder && (
                                <p className={`mh-status mh-status--${reminder.kind}`}>{reminder.label}</p>
                            )}
                        </>
                    )}
                    <div className="mh-status__actions">
                        {!noAccess && (
                            <button type="button" className="btn btn-link" onClick={() => setIsEditMode(true)}>
                                {neverAsked ? "Record answer" : "Edit"}
                            </button>
                        )}

                        {isSuperAdmin && !neverAsked && !noAccess && (
                            <button type="button" className="btn btn-link" onClick={toggleHistory}>
                                {isHistoryOpen ? "Hide history" : "Change history"}
                            </button>
                        )}

                        {!noAccess && canCancelReminder && reminder && reminder.kind === "pending" && (
                            <button
                                type="button"
                                className="btn btn-link text-danger"
                                disabled={isCancelling}
                                onClick={() => {
                                    // The email cannot be recalled once the scheduler has taken it, so
                                    // make the clinician mean it. The answer itself is kept either way.
                                    if (window.confirm(
                                        "Cancel this patient's follow-up reminder email?\n\n"
                                        + "The mental health answer stays on the record. Only the "
                                        + "pending email is stopped, and it cannot be rescheduled "
                                        + "without editing the answer again."
                                    )) {
                                        cancelReminder();
                                    }
                                }}
                            >
                                {isCancelling ? "Cancelling…" : "Cancel reminder"}
                            </button>
                        )}
                    </div>

                    {isSuperAdmin && isHistoryOpen && (
                        <div className="mh-history" data-testid="mh-history">
                            {historyError && <p className="text-danger">{historyError}</p>}
                            {changes && changes.length === 0 && <p className="text-muted">No changes recorded yet.</p>}
                            {changes && changes.length > 0 && (
                                <ul className="list-unstyled mb-0">
                                    {changes.map(change => (
                                        <li key={change.id} className="mb-2">
                                            <div className="text-muted font-size-12">
                                                {formatUkDateTime(change.changedAt)}
                                                {" - "}
                                                {change.changedBy
                                                    ? `${change.changedBy.firstName || ""} ${change.changedBy.lastName || ""}`.trim()
                                                    : "ClinicDesk / system"}
                                            </div>
                                            <div>
                                                {change.action === "cancelled"
                                                    ? "Cancelled the reminder email."
                                                    : `${change.action === "recorded" ? "Recorded" : "Changed"}: `
                                                        + `${describeHistoryState(change.before)} → ${describeHistoryState(change.after)}`}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}

            {pendingChange && (
                <Modal isOpen={true} centered={true} className="modal-size">
                    <Card className="p-4 mb-0" data-testid="mh-confirm">
                        <header className="font-size-22">Confirm the mental health change</header>
                        <dl className="mt-3 mb-0">
                            <dt>Patient</dt>
                            <dd data-testid="mh-confirm-patient">{patientName || "-"}</dd>
                            <dt>Consultation</dt>
                            <dd>{appointment.date ? displayUkDate(appointment.date) : "-"}</dd>
                            <dt>Now</dt>
                            <dd data-testid="mh-confirm-from">{pendingChange.summary.from}</dd>
                            <dt>After this change</dt>
                            <dd data-testid="mh-confirm-to">{pendingChange.summary.to}</dd>
                            <dt>Email to the patient</dt>
                            <dd data-testid="mh-confirm-email">{pendingChange.summary.email}</dd>
                        </dl>
                        {pendingChange.summary.notes.map(note => (
                            <p key={note} className="mb-1 text-warning" data-testid="mh-confirm-note">{note}</p>
                        ))}
                        <p className="text-muted font-size-12 mt-2 mb-0">
                            This change is recorded in the answer&apos;s history with your name.
                        </p>
                        <section className="d-flex flex-row justify-content-between">
                            <button
                                type="button"
                                className="btn btn-primary w-100 mt-4 mr-3"
                                disabled={isSaving}
                                onClick={() => submit(pendingChange.payload)}
                            >
                                {isSaving ? "Saving…" : "Confirm change"}
                            </button>
                            <button
                                type="button"
                                className="btn btn-light w-100 mt-4 ml-3"
                                disabled={isSaving}
                                onClick={() => setPendingChange(null)}
                            >
                                Go back
                            </button>
                        </section>
                    </Card>
                </Modal>
            )}

            {isEditMode && (
                <div className="mh-section">
                    <div className="mh-options" role="radiogroup" aria-label="Mental health consultation">
                        {[{ value: false, label: "No" }, { value: true, label: "Yes" }].map(opt => (
                            <label
                                key={String(opt.value)}
                                className={`mh-option${isMentalHealth === opt.value ? " mh-option--selected" : ""}`}
                            >
                                <input
                                    type="radio"
                                    name="pm-mh"
                                    checked={isMentalHealth === opt.value}
                                    onChange={() => {
                                        setIsMentalHealth(opt.value);
                                        setError("");

                                        if (!opt.value) {
                                            setPlan(null);
                                            setFollowUpWeeks(null);
                                            setCustomFollowUpDate("");
                                        }
                                    }}
                                />
                                <span>{opt.label}</span>
                            </label>
                        ))}
                    </div>

                    {isMentalHealth && (
                        <>
                            <span className="consultation-label mh-section__question">Follow-up plan</span>

                            <div className="mh-options" role="radiogroup" aria-label="Follow-up plan">
                                {[
                                    { value: MENTAL_HEALTH_PLAN.followUp, label: "Follow-up needed" },
                                    { value: MENTAL_HEALTH_PLAN.repeatPlan, label: "Join repeat prescription plan" },
                                ].map(opt => (
                                    <label
                                        key={opt.value}
                                        className={`mh-option mh-option--wide${plan === opt.value ? " mh-option--selected" : ""}`}
                                    >
                                        <input
                                            type="radio"
                                            name="pm-mh-plan"
                                            checked={plan === opt.value}
                                            onChange={() => {
                                                setPlan(opt.value);
                                                setError("");

                                                if (opt.value !== MENTAL_HEALTH_PLAN.followUp) {
                                                    setFollowUpWeeks(null);
                                                    setCustomFollowUpDate("");
                                                }
                                            }}
                                        />
                                        <span>{opt.label}</span>
                                    </label>
                                ))}
                            </div>
                        </>
                    )}

                    {isMentalHealth && plan === MENTAL_HEALTH_PLAN.followUp && (
                        <>
                            <span className="consultation-label mh-section__question">Follow up in</span>

                            <div className="mh-options" role="radiogroup" aria-label="Follow up in">
                                {FOLLOW_UP_DURATIONS.map(opt => (
                                    <label
                                        key={String(opt.value)}
                                        className={`mh-option${followUpWeeks === opt.value ? " mh-option--selected" : ""}`}
                                    >
                                        <input
                                            type="radio"
                                            name="pm-mh-weeks"
                                            checked={followUpWeeks === opt.value}
                                            onChange={() => {
                                                setFollowUpWeeks(opt.value);
                                                setError("");

                                                if (opt.value !== OTHER_DURATION) {
                                                    setCustomFollowUpDate("");
                                                }
                                            }}
                                        />
                                        <span>{opt.label}</span>
                                    </label>
                                ))}
                            </div>

                            {followUpWeeks === OTHER_DURATION && (
                                <input
                                    className="form-control mh-section__date"
                                    type="date"
                                    aria-label="Follow-up date"
                                    value={customFollowUpDate}
                                    min={earliestFollowUpDate}
                                    onChange={e => { setCustomFollowUpDate(e.target.value); setError(""); }}
                                />
                            )}
                        </>
                    )}

                    {error && <div className="mh-section__error" role="alert">{error}</div>}

                    <div className="mh-section__actions">
                        <button type="button" className="btn btn-primary" onClick={save}>Save</button>
                        <button
                            type="button"
                            className="btn btn-link"
                            onClick={() => { setIsEditMode(false); setError(""); load(); }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {!isEditMode && error && <div className="text-danger" role="alert">{error}</div>}
        </Card>
    );
};
